import type * as CSS from 'csstype';
import type {
  RequiredTheme,
  ResponsiveValue,
  Theme,
  TLengthStyledSystem,
} from 'styled-system';

import createCustomParser from './createCustomParser';

/**
 * Adds the text-decoration CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration
 */
export interface TextDecorationProps {
  textDecoration?: ResponsiveValue<CSS.Property.TextDecoration>;
}

export const textDecoration = createCustomParser({
  textDecoration: true,
});

/**
 * Adds the text-rendering CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/text-rendering
 */
export interface TextRenderingProps {
  textRendering?: ResponsiveValue<CSS.Property.TextRendering>;
}

export const textRendering = createCustomParser({
  textRendering: true,
});

/**
 * Adds the text-transform CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/text-transform
 */
export interface TextTransformProps {
  textTransform?: ResponsiveValue<CSS.Property.TextTransform>;
}

export const textTransform = createCustomParser({
  textTransform: true,
});

/**
 * Adds the text-wrap CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/text-wrap
 */
export interface TextWrapProps {
  textWrap?: ResponsiveValue<CSS.Property.TextWrap>;
}

export const textWrap = createCustomParser({
  textWrap: true,
});

/**
 * Adds the user-select CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/user-select
 */
export interface UserSelectProps {
  userSelect?: ResponsiveValue<CSS.Property.UserSelect>;
}

export const userSelect = createCustomParser({
  userSelect: true,
});

/**
 * Adds the appearance CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/appearance
 */
export interface AppearanceProps {
  appearance?: ResponsiveValue<
    | CSS.Property.Appearance
    | CSS.Property.WebkitAppearance
    | CSS.Property.MozAppearance
  >;
}

export const appearance = createCustomParser({
  appearance: {
    properties: ['-webkit-appearance', '-moz-appearance', 'appearance'],
  },
});

/**
 * Adds the white-space CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/white-space
 */
export interface WhiteSpaceProps {
  whiteSpace?: ResponsiveValue<CSS.Property.WhiteSpace>;
}

export const whiteSpace = createCustomParser({
  whiteSpace: true,
});

/**
 * Overrides Styled System builtin. We can't use border shorthand,
 * see Theme interface.
 */
export interface BorderOverrideProps<ThemeType extends Theme = RequiredTheme> {
  border?:
    | ResponsiveValue<CSS.Property.BorderWidth<TLengthStyledSystem>, ThemeType>
    | undefined;
  borderTop?:
    | ResponsiveValue<
        CSS.Property.BorderTopWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
  borderRight?:
    | ResponsiveValue<
        CSS.Property.BorderRightWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
  borderLeft?:
    | ResponsiveValue<
        CSS.Property.BorderLeftWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
  borderBottom?:
    | ResponsiveValue<
        CSS.Property.BorderBottomWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
  borderX?:
    | ResponsiveValue<
        | CSS.Property.BorderLeftWidth<TLengthStyledSystem>
        | CSS.Property.BorderRightWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
  borderY?:
    | ResponsiveValue<
        | CSS.Property.BorderTopWidth<TLengthStyledSystem>
        | CSS.Property.BorderBottomWidth<TLengthStyledSystem>,
        ThemeType
      >
    | undefined;
}

export const borderShorthandOverride = createCustomParser({
  border: {
    property: 'borderWidth',
    scale: 'borderWidths',
  },
  borderTop: {
    property: 'borderTopWidth',
    scale: 'borderWidths',
  },
  borderRight: {
    property: 'borderRightWidth',
    scale: 'borderWidths',
  },
  borderLeft: {
    property: 'borderLeftWidth',
    scale: 'borderWidths',
  },
  borderBottom: {
    property: 'borderBottomWidth',
    scale: 'borderWidths',
  },
  borderX: {
    properties: ['borderLeftWidth', 'borderRightWidth'],
    scale: 'borderWidths',
  },
  borderY: {
    properties: ['borderTopWidth', 'borderBottomWidth'],
    scale: 'borderWidths',
  },
});

export interface GapProps<ThemeType extends Theme = RequiredTheme> {
  gap?:
    | ResponsiveValue<CSS.Property.Gap<TLengthStyledSystem>, ThemeType>
    | undefined;
  columnGap?:
    | ResponsiveValue<CSS.Property.ColumnGap<TLengthStyledSystem>, ThemeType>
    | undefined;
  rowGap?:
    | ResponsiveValue<CSS.Property.RowGap<TLengthStyledSystem>, ThemeType>
    | undefined;
}

export interface GridProps extends GapProps {
  grid?: ResponsiveValue<CSS.Property.Grid>;
  gridAutoFlow?: ResponsiveValue<CSS.Property.GridAutoFlow>;
  gridAutoColumns?: ResponsiveValue<CSS.Property.GridAutoColumns>;
  gridAutoRows?: ResponsiveValue<CSS.Property.GridAutoRows>;
  gridTemplate?: ResponsiveValue<CSS.Property.GridTemplate>;
  gridTemplateColumns?: ResponsiveValue<CSS.Property.GridTemplateColumns>;
  gridTemplateRows?: ResponsiveValue<CSS.Property.GridTemplateRows>;
  gridTemplateAreas?: ResponsiveValue<CSS.Property.GridTemplateAreas>;
  justifyItems?: ResponsiveValue<CSS.Property.JustifyItems>;
  alignItems?: ResponsiveValue<CSS.Property.AlignItems>;
  placeItems?: ResponsiveValue<CSS.Property.PlaceItems>;
  justifyContent?: ResponsiveValue<CSS.Property.JustifyContent>;
  alignContent?: ResponsiveValue<CSS.Property.AlignContent>;
  placeContent?: ResponsiveValue<CSS.Property.PlaceContent>;
}

const gapConfig = {
  gap: {
    property: 'gap',
    scale: 'space',
  },
  columnGap: {
    property: 'columnGap',
    scale: 'space',
  },
  rowGap: {
    property: 'rowGap',
    scale: 'space',
  },
};

export const gap = createCustomParser(gapConfig);

export const grid = createCustomParser({
  grid: true,
  gridAutoFlow: true,
  gridAutoColumns: true,
  gridAutoRows: true,
  gridTemplate: true,
  gridTemplateColumns: true,
  gridTemplateRows: true,
  gridTemplateAreas: true,
  justifyItems: true,
  alignItems: true,
  placeItems: true,
  justifyContent: true,
  alignContent: true,
  placeContent: true,
  ...gapConfig,
});

export interface GridItemProps {
  gridColumnStart?: ResponsiveValue<CSS.Property.GridColumnStart>;
  gridColumnEnd?: ResponsiveValue<CSS.Property.GridColumnEnd>;
  gridRowStart?: ResponsiveValue<CSS.Property.GridRowStart>;
  gridRowEnd?: ResponsiveValue<CSS.Property.GridRowEnd>;
  gridColumn?: ResponsiveValue<CSS.Property.GridColumn>;
  gridRow?: ResponsiveValue<CSS.Property.GridRow>;
  gridArea?: ResponsiveValue<CSS.Property.GridArea>;
  justifySelf?: ResponsiveValue<CSS.Property.JustifySelf>;
  alignSelf?: ResponsiveValue<CSS.Property.AlignSelf>;
  placeSelf?: ResponsiveValue<CSS.Property.PlaceSelf>;
}

export const gridItem = createCustomParser({
  gridColumnStart: true,
  gridColumnEnd: true,
  gridRowStart: true,
  gridRowEnd: true,
  gridColumn: true,
  gridRow: true,
  gridArea: true,
  justifySelf: true,
  alignSelf: true,
  placeSelf: true,
});

/**
 * Adds transform CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/transform
 */
export const transform = createCustomParser({
  transform: true,
});

export interface TransformProps {
  transform?: ResponsiveValue<CSS.Property.Transform>;
}

/**
 * Adds cursor CSS property
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/cursor
 */
export const cursor = createCustomParser({
  cursor: true,
});

export interface CursorProps {
  cursor?: ResponsiveValue<CSS.Property.Cursor>;
}
